import { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { Timer, AlertTriangle } from "lucide-react";

interface SessionTimeoutWarningProps {
  isVisible: boolean;
  onExtend: () => void;
  onTimeout: () => void;
  countdownSeconds?: number;
}

export const SessionTimeoutWarning = ({
  isVisible,
  onExtend,
  onTimeout,
  countdownSeconds = 60,
}: SessionTimeoutWarningProps) => {
  const [remainingSeconds, setRemainingSeconds] = useState(countdownSeconds);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset contador quando modal aparece
  useEffect(() => {
    if (isVisible) {
      setRemainingSeconds(countdownSeconds);
    }
  }, [isVisible, countdownSeconds]);

  // Contagem regressiva
  useEffect(() => {
    if (!isVisible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          onTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, onTimeout]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressValue = (remainingSeconds / countdownSeconds) * 100;

  const handleExtend = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRemainingSeconds(countdownSeconds);
    onExtend();
  };

  return (
    <AlertDialog open={isVisible}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Sessão Prestes a Expirar
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>
              Sua sessão será encerrada automaticamente por inatividade.
              Clique no botão abaixo para continuar trabalhando.
            </p>

            <div className="flex flex-col items-center gap-3 py-4">
              <div className="flex items-center gap-2 text-3xl font-bold text-foreground">
                <Timer className="h-8 w-8 text-amber-500" />
                <span className={remainingSeconds <= 10 ? "text-red-600 animate-pulse" : ""}>
                  {formatTime(remainingSeconds)}
                </span>
              </div>
              <Progress 
                value={progressValue} 
                className={`w-full h-2 ${remainingSeconds <= 10 ? "[&>div]:bg-red-500" : "[&>div]:bg-amber-500"}`}
              />
              <p className="text-xs text-muted-foreground">
                Tempo restante antes do logout automático
              </p>
            </div>

            <p className="text-sm text-muted-foreground">
              Para sua segurança, o sistema encerra automaticamente sessões inativas.
              Você pode configurar o tempo de inatividade nas preferências do usuário.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={handleExtend}
            className="w-full bg-primary hover:bg-primary/90"
          >
            Continuar Conectado
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
